/**
 * GiftCardLedgerCard — "Ledger": every credit and debit, newest first, with a
 * running balance per row (BON-223). The credits and debits shown here always
 * sum to the summary card's balance — the footer restates it as the final
 * check.
 */
import { formatMoney } from '../review/reviewQueueModel';

import { GiftCardLedgerRow } from './GiftCardLedgerRow';
import { ledgerRecordsLabel, runningBalances } from './giftCardsModel';
import type { GiftCardDetail } from './giftCardTypes';

export function GiftCardLedgerCard({ card }: { card: GiftCardDetail }) {
  const led = card.transactions;
  const running = runningBalances(led);
  return (
    <section className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          padding: '12px 16px',
          borderBottom: '1px solid var(--nb-border)',
        }}
      >
        <h3
          className="nb-display"
          style={{ margin: 0, fontSize: 15, color: 'var(--nb-bark-dark)' }}
        >
          Ledger
        </h3>
        <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>
          {ledgerRecordsLabel(led.length)}
        </span>
      </div>

      {led.length === 0 ? (
        <div style={{ padding: '18px 16px', fontSize: 12.5, color: 'var(--nb-subtle)' }}>
          No activity recorded for this card yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {led.map((entry, i) => (
            <GiftCardLedgerRow
              key={entry.id}
              entry={entry}
              balanceCents={running[i]}
              currency={card.currency}
              last={i === led.length - 1}
            />
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 16px',
          borderTop: '1px solid var(--nb-border)',
          background: 'var(--nb-surface)',
        }}
      >
        <span
          style={{
            flex: 1,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            color: 'var(--nb-subtle)',
          }}
        >
          Ledger balance
        </span>
        <span
          className="nb-tabular"
          style={{ fontSize: 14, fontWeight: 800, color: 'var(--nb-ink)' }}
        >
          {formatMoney(card.balanceCents, card.currency)}
        </span>
      </div>
    </section>
  );
}
